import { getRoleDisplayName } from './roleUtils';

const LEGACY_UPDATE_ACTIONS = new Set([
  'update',
  'update_patient',
  'update_meter',
  'update_schedule_targets',
]);

const LEGACY_CREATE_ACTIONS = new Set([
  'create',
  'add_meter',
]);

const LEGACY_DELETE_ACTIONS = new Set([
  'delete',
  'delete_meter',
]);

export function normalizeEventLogAction(action) {
  const raw = String(action || '').trim().toLowerCase();
  if (!raw) return '';
  if (LEGACY_CREATE_ACTIONS.has(raw)) return 'create';
  if (LEGACY_UPDATE_ACTIONS.has(raw)) return 'update';
  if (LEGACY_DELETE_ACTIONS.has(raw)) return 'delete';
  if (raw === 'read' || raw === 'view') return raw === 'view' ? 'view' : 'read';
  if (raw === 'export_pdf' || raw === 'export-pdf') return 'export_pdf';
  if (raw === 'export_csv' || raw === 'export-csv') return 'export_csv';
  if (raw === 'email_report' || raw === 'email-report' || raw === 'email') return 'email_report';
  return raw;
}

export function formatActionLabel(action) {
  const normalized = normalizeEventLogAction(action);
  const labels = {
    create: 'Create',
    read: 'View',
    view: 'View',
    update: 'Update',
    delete: 'Delete',
    export_pdf: 'Export PDF',
    export_csv: 'Export CSV',
    email_report: 'Email Report',
    email: 'Email Report',
  };
  if (labels[normalized]) return labels[normalized];

  if (!action) return '—';
  return String(action)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function moduleDisplayName(moduleId) {
  const map = {
    Patient: 'Patient',
    'Patient Schedule': 'Schedule & targets',
    'Patient Meter': 'Meter mapping',
    'Patient Document': 'Uploaded documents',
    'Patient Follow-up': 'Follow-up',
    'Patient Care Plan': 'Care Plan',
    'Patient Vital': 'Vitals',
    'Patient Export': 'Export',
    Billing: 'Billing',
    Inventory: 'Inventory',
    Practice: 'Practice',
  };
  return map[moduleId] || moduleId || 'Patient';
}

const USER_TARGET_MODULE_PATTERNS = [
  /^patient/i,
  /^admin$/i,
  /^provider$/i,
  /^caregiver$/i,
  /^system caregiver$/i,
];

export function isPatientEventLogModule(moduleId) {
  const raw = String(moduleId || '').trim();
  return /^patient/i.test(raw);
}

export function isUserTargetEventLogModule(moduleId) {
  const raw = String(moduleId || '').trim();
  return USER_TARGET_MODULE_PATTERNS.some((pattern) => pattern.test(raw));
}

/**
 * @param {string|null|undefined} firstName
 * @param {string|null|undefined} lastName
 * @param {string|null|undefined} username
 */
export function formatPersonWithUsername(firstName, lastName, username) {
  const name = [firstName, lastName]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(' ');
  const user = String(username || '').trim();

  if (name && user) return `${name} (${user})`;
  if (name) return name;
  if (user) return user;
  return '';
}

/**
 * @param {object|null|undefined} log
 */
export function formatPerformedByLabel(log) {
  if (!log) return 'System';

  const role =
    String(log.performer_role_name || '').trim() ||
    getRoleDisplayName(log.performer_role_id);

  const personLabel = formatPersonWithUsername(
    log.performer_first_name,
    log.performer_last_name,
    log.performer_username
  );

  const fallbackName = String(log.performer_name || '').trim();
  const personPart =
    personLabel ||
    (fallbackName && log.performer_username
      ? `${fallbackName} (${String(log.performer_username).trim()})`
      : fallbackName);

  if (!role && !personPart) return 'System';
  if (role && personPart) return `${role} - ${personPart}`;
  return role || personPart || 'System';
}

export function parseEventLogJson(value) {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
}

export function extractPracticeNameFromLog(log) {
  if (!log) return '';
  const fromColumn = String(log.practice_name || '').trim();
  if (fromColumn) return fromColumn;
  const updateData = parseEventLogJson(log.update_data);
  const oldData = parseEventLogJson(log.data);
  return String(updateData.practice_name || oldData.practice_name || '').trim();
}

export function extractProductNameFromLog(log) {
  if (!log) return '';
  const updateData = parseEventLogJson(log.update_data);
  const oldData = parseEventLogJson(log.data);
  const name =
    updateData.product_name ||
    oldData.product_name ||
    updateData.name ||
    oldData.name ||
    '';
  return String(name).trim();
}

const SCHEDULE_SECTION_LABELS = [
  { keys: ['dailySchedule'], label: 'Daily Schedule' },
  { keys: ['bloodPressureTimePeriod', 'bloodPressureTargetRange'], label: 'Blood Pressure' },
  { keys: ['bloodGlucoseTimePeriod', 'bloodGlucoseTargetRange'], label: 'Blood Glucose' },
  { keys: ['weightTimePeriod', 'weightTargetRange'], label: 'Weight' },
];

/** Which schedule/target section(s) a log touched (Blood Pressure, Blood Glucose, etc.). */
export function extractScheduleSectionsFromLog(log) {
  if (!log) return [];
  const updateData = parseEventLogJson(log.update_data);
  const oldData = parseEventLogJson(log.data);
  const keys = new Set([
    ...Object.keys(updateData || {}),
    ...Object.keys(oldData || {}),
  ]);
  return SCHEDULE_SECTION_LABELS
    .filter((section) => section.keys.some((key) => keys.has(key)))
    .map((section) => section.label);
}

/**
 * @param {string} moduleId
 * @param {{
 *   patientUsername?: string|null,
 *   targetFirstName?: string|null,
 *   targetLastName?: string|null,
 *   log?: object|null,
 * }} [options]
 */
export function formatPerformedOnLabel(moduleId, options = {}) {
  const { patientUsername, targetFirstName, targetLastName, log } = options;
  const effectiveModule = log?.module || moduleId;
  const moduleKey = String(effectiveModule || '').toLowerCase();

  // Schedule & Targets: show Blood Pressure / Blood Glucose / Weight / Daily Schedule
  if (moduleKey === 'schedule' || moduleKey === 'patient schedule') {
    const sections = extractScheduleSectionsFromLog(log);
    if (sections.length) {
      if (moduleKey === 'patient schedule') {
        const personLabel = formatPersonWithUsername(
          log?.target_first_name ?? targetFirstName ?? null,
          log?.target_last_name ?? targetLastName ?? null,
          log?.target_username ||
            log?.target_patient_username ||
            patientUsername ||
            null
        );
        if (personLabel) return `${personLabel} — ${sections.join(', ')}`;
      }
      return sections.join(', ');
    }
  }

  if (!isUserTargetEventLogModule(effectiveModule)) {
    if (moduleKey === 'practice') {
      const practiceName = extractPracticeNameFromLog(log);
      if (practiceName) return practiceName;
    }
    if (moduleKey === 'inventory') {
      const productName = extractProductNameFromLog(log);
      if (productName) return productName;
    }
    return moduleDisplayName(effectiveModule);
  }

  const firstName = log?.target_first_name ?? targetFirstName ?? null;
  const lastName = log?.target_last_name ?? targetLastName ?? null;
  const username =
    log?.target_username ||
    log?.target_patient_username ||
    patientUsername ||
    null;

  const label = formatPersonWithUsername(firstName, lastName, username);
  if (label) return label;

  return moduleDisplayName(effectiveModule);
}

/** Parse DB/API datetime and return a Date in the viewer's local timezone. */
export function parseEventLogDateTime(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();

  // ISO 8601 with Z or explicit offset — absolute UTC instant.
  if (/[Tt].*(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  // MySQL DATETIME without offset — DB stores UTC (RDS / UTC_TIMESTAMP).
  const mysqlMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?$/
  );
  if (mysqlMatch) {
    const [, y, mo, d, h = '0', mi = '0', s = '0'] = mysqlMatch;
    return new Date(Date.UTC(+y, +mo - 1, +d, +h, +mi, +s));
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function formatEventLogDateTime(value) {
  const d = parseEventLogDateTime(value);
  if (!d) return 'N/A';

  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  let hours = d.getHours();
  const am = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${mm}/${dd}/${yy} ${hours}:${mi} ${am}`;
}
